/**
 * FieldFork
 *
 * Anchored popover container for the field-level "fork" UI. Hosts a
 * ParameterValueSourceTabs strip, a dismiss button, and a render-prop
 * body that branches by the currently selected source.
 *
 * The popover positions itself relative to a captured anchor rect.
 * It opens below-end by default, flipping above when there isn't
 * enough room. It is not modal — page scroll continues to work.
 *
 * Position math runs once on mount (and on viewport resize) using a
 * `useLayoutEffect` so first paint already reflects the clamped
 * coordinates. No external positioning library is used.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import {
  Button,
  Portal,
  mergeClasses,
} from '@coreai/fluentui-react';
import { DismissRegular } from '@fluentui/react-icons';
import {
  ParameterValueSourceTabs,
  type ParameterValueSourceTabsHandle,
  type ValueSource,
} from '../shared/ParameterValueSourceTabs';
import { buildUseFieldForkStyles } from './FieldFork.styles';

export interface FieldForkAnchorRect {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

export interface FieldForkProps {
  /**
   * Anchor element's bounding rect captured at open time. Popover
   * positions below-end relative to this.
   */
  anchor: FieldForkAnchorRect;
  /** Initial tab. */
  initialSource: ValueSource;
  /** Called when the user dismisses (Esc, outside click, or close button). */
  onClose: () => void;
  /**
   * Optional notifier fired when the user picks a tab inside the popover.
   * The popover still owns the `selected` state internally — this is for
   * consumers that want to mirror it into store / telemetry.
   */
  onSelectSource?: (source: ValueSource) => void;
  /**
   * Children render the per-tab body. Receive the current selected tab so
   * they can branch.
   */
  children: (selected: ValueSource) => ReactNode;
}

const POPOVER_WIDTH = 520;
const POPOVER_MAX_HEIGHT = 480;
/** Minimum height we still try to render rather than degenerating to 0. */
const POPOVER_MIN_HEIGHT = 160;
const VIEWPORT_MARGIN = 8;
const ANCHOR_GAP = 6;

// makeStyles hook bound to the popover's structural dimensions.
// Built outside the component so the hook reference stays stable
// across renders (griffel caches by the hook identity).
const useStyles = buildUseFieldForkStyles({
  width: POPOVER_WIDTH,
  maxHeight: POPOVER_MAX_HEIGHT,
});

type Placement = 'below' | 'above';

interface ResolvedPosition {
  top: number;
  left: number;
  placement: Placement;
  /** Effective max height after honoring viewport bounds and the chosen side's room. */
  maxHeight: number;
}

const resolvePosition = (anchor: FieldForkAnchorRect): ResolvedPosition => {
  const viewportWidth =
    typeof window !== 'undefined' ? window.innerWidth : POPOVER_WIDTH;
  const viewportHeight =
    typeof window !== 'undefined' ? window.innerHeight : POPOVER_MAX_HEIGHT;

  const spaceBelow = Math.max(
    0,
    viewportHeight - anchor.bottom - ANCHOR_GAP - VIEWPORT_MARGIN,
  );
  const spaceAbove = Math.max(
    0,
    anchor.top - ANCHOR_GAP - VIEWPORT_MARGIN,
  );
  const placement: Placement =
    spaceBelow >= POPOVER_MAX_HEIGHT || spaceBelow >= spaceAbove
      ? 'below'
      : 'above';

  const available = placement === 'below' ? spaceBelow : spaceAbove;
  // Honor POPOVER_MIN_HEIGHT so the popover is usable, but cap by what the
  // viewport can actually fit — otherwise on tiny viewports the minimum
  // would push the popover past the screen edge.
  const viewportCap = Math.max(0, viewportHeight - 2 * VIEWPORT_MARGIN);
  const maxHeight = Math.min(
    POPOVER_MAX_HEIGHT,
    viewportCap,
    Math.max(POPOVER_MIN_HEIGHT, available),
  );

  let top =
    placement === 'below'
      ? anchor.bottom + ANCHOR_GAP
      : Math.max(VIEWPORT_MARGIN, anchor.top - ANCHOR_GAP - maxHeight);
  // Final guard: when `maxHeight` was lifted to POPOVER_MIN_HEIGHT despite
  // `available` being smaller (anchor near a viewport edge), the popover
  // would overflow without this clamp. Overlapping the anchor is the
  // lesser evil compared to clipping off-screen.
  const maxTop = viewportHeight - VIEWPORT_MARGIN - maxHeight;
  if (top > maxTop) top = Math.max(VIEWPORT_MARGIN, maxTop);

  let left = anchor.right - POPOVER_WIDTH;
  const maxLeft = viewportWidth - POPOVER_WIDTH - VIEWPORT_MARGIN;
  if (left > maxLeft) left = maxLeft;
  if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;

  return { top, left, placement, maxHeight };
};

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Field-anchored popover hosting tabs + tab body.
 */
export const FieldFork = ({
  anchor,
  initialSource,
  onClose,
  onSelectSource,
  children,
}: FieldForkProps) => {
  const styles = useStyles();
  // Fluent's `Portal` mounts into the FluentProvider's `targetDocument` and
  // re-applies theme + Griffel renderer to the portaled subtree, so token
  // CSS variables resolve correctly without re-mounting a FluentProvider.
  const popoverRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<ParameterValueSourceTabsHandle>(null);
  const [selected, setSelected] = useState<ValueSource>(initialSource);
  const [position, setPosition] = useState<ResolvedPosition>(() =>
    resolvePosition(anchor),
  );
  const [open, setOpen] = useState(false);

  useLayoutEffect(() => {
    setPosition(resolvePosition(anchor));
  }, [anchor]);

  useEffect(() => {
    const handleResize = () => setPosition(resolvePosition(anchor));
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [anchor]);

  // Trigger the open animation on the next frame so the initial styles
  // (opacity 0, scale 0.96) are committed before transitioning.
  useEffect(() => {
    const id = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Move focus into the popover on mount.
  useEffect(() => {
    tabsRef.current?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key === 'Tab') {
        const root = popoverRef.current;
        if (!root) return;
        const focusable = Array.from(
          root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
        ).filter((el) => !el.hasAttribute('disabled') && el.tabIndex !== -1);
        if (focusable.length === 0) {
          event.preventDefault();
          return;
        }
        const first = focusable[0]!;
        const last = focusable[focusable.length - 1]!;
        const active = document.activeElement as HTMLElement | null;
        if (event.shiftKey) {
          if (active === first || !root.contains(active)) {
            event.preventDefault();
            last.focus();
          }
        } else {
          if (active === last) {
            event.preventDefault();
            first.focus();
          }
        }
      }
    },
    [onClose],
  );

  // Outside-click closes. Listen on capture to short-circuit before any
  // descendant click handlers run.
  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const root = popoverRef.current;
      if (!root) return;
      const target = event.target as Node | null;
      if (target && !root.contains(target)) {
        onClose();
      }
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [onClose]);

  const stopInsidePointerDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
    },
    [],
  );

  const handleSelect = useCallback(
    (next: ValueSource) => {
      setSelected(next);
      onSelectSource?.(next);
    },
    [onSelectSource],
  );

  return (
    <Portal>
      <div
        ref={popoverRef}
        role="dialog"
        aria-modal="false"
        aria-label="Insert value"
        tabIndex={-1}
        className={mergeClasses(
          styles.popover,
          position.placement === 'above' && styles.popoverAbove,
          open && styles.popoverOpen,
        )}
        style={{ top: position.top, left: position.left, maxHeight: position.maxHeight }}
        onKeyDown={handleKeyDown}
        onPointerDown={stopInsidePointerDown}
      >
        <div className={styles.header}>
          <div className={styles.tabsWrapper}>
            <ParameterValueSourceTabs
              ref={tabsRef}
              selected={selected}
              onSelect={handleSelect}
            />
          </div>
          <Button
            appearance="subtle"
            icon={<DismissRegular />}
            aria-label="Close"
            className={styles.closeButton}
            onClick={onClose}
          />
        </div>
        <div className={styles.body}>{children(selected)}</div>
      </div>
    </Portal>
  );
};
